/**
 * Secret Chat REST API client.
 *
 * Thin `fetch` wrapper that exposes the five backend HTTP endpoints
 * (`join`, `sendMessage`, `listMessages`, `markRead`, `negotiate`) and maps
 * every server response — success or failure — into a uniform
 * `ApiResult<T>` so callers never have to inspect raw `Response`/HTTP-code
 * tuples or write try/catch blocks for expected business outcomes.
 *
 * Design contract (`design.md` §Function Handler Layer):
 *   - URL shape:       `${baseUrl}/api/...` (default baseUrl `''` = same origin)
 *   - JSON in, JSON out for all POST endpoints
 *   - Status code → ChatApiError kind mapping is defined in `mapErrorStatus`.
 *
 * Dependency direction (§File Structure Plan):
 *   - This file is the *only* network layer for the SPA. Hooks/components
 *     consume it through the `ChatApi` interface and never call `fetch`
 *     directly. The factory accepts a `fetch` injection so unit tests
 *     (`chatApi.test.ts`) stay hermetic.
 *
 * Manual sync rule: the DTO types below mirror `api/src/domain/types.ts`
 * (server) and the wire-format subset in `web/src/lib/types.ts`. JSON
 * cannot carry TypeScript brand types so primitives are flattened to
 * `string` / `number`. When the server contract changes, update both
 * files in the same change set.
 */

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

/**
 * Discriminated success/failure container. Frontend code switches on
 * `result.ok` to either consume `value` or render the typed `error`.
 *
 * Defined locally (rather than reusing `Result<T,E>` from `lib/types.ts`)
 * so the API surface remains self-contained: `ChatApi` callers don't need
 * to import a separate module just to read the result shape.
 */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ChatApiError };

// ---------------------------------------------------------------------------
// Error model
// ---------------------------------------------------------------------------

/**
 * Every failure mode the client can observe, expressed as a discriminated
 * union over `kind`. UI code maps `kind` to a localized message; logging
 * code keeps `status`/`detail` for diagnostics.
 *
 * The kinds correspond to the HTTP statuses returned by handlers in
 * `api/src/functions/*`:
 *   - 400 → invalidInput      (server includes a `ValidationError` detail)
 *   - 403 → notJoined         (sendMessage/markRead: caller not in room)
 *   - 404 → roomNotFound      (sendMessage/markRead: stale or unknown room)
 *   - 409 → roomFull          (join: third nickname rejected)
 *   - 429 → rateLimited       (joinRoom/sendMessage/negotiate)
 *   - 503 → concurrencyConflict (join/markRead: Cosmos ETag race)
 *   - other → serverError     (catch-all; preserves status + raw detail)
 *   - fetch threw → networkError
 */
export type ChatApiError =
  | { readonly kind: 'invalidInput'; readonly status: 400; readonly detail?: unknown }
  | { readonly kind: 'notJoined'; readonly status: 403 }
  | { readonly kind: 'roomNotFound'; readonly status: 404 }
  | { readonly kind: 'roomFull'; readonly status: 409 }
  | {
      readonly kind: 'guestLimitReached';
      readonly status: 423;
      readonly limit: number;
    }
  | {
      readonly kind: 'bannedWord';
      readonly status: 422;
      readonly word: string;
    }
  | { readonly kind: 'rateLimited'; readonly status: 429; readonly retryAfterSeconds: number }
  | {
      readonly kind: 'concurrencyConflict';
      readonly status: 503;
      readonly retryAfterSeconds: number;
    }
  | { readonly kind: 'serverError'; readonly status: number; readonly detail?: unknown }
  | { readonly kind: 'networkError'; readonly cause: unknown };

// ---------------------------------------------------------------------------
// Wire DTOs
// ---------------------------------------------------------------------------

/** Request body for `POST /api/rooms/{roomId}/join`.
 *
 * `userId` is intentionally optional. The server (`api/src/functions/joinRoom.ts`)
 * never reads it from the body and recomputes the canonical SignalR userId
 * from `(roomId, nickname)` itself, so the wire contract treats it as
 * informational/audit-only. Callers that have no audit need omit it; the
 * sole authoritative computation lives server-side. (See tasks.md
 * Implementation Notes 4.3-1 / 6.1.)
 */
export interface JoinRoomRequest {
  readonly nickname: string;
  readonly userId?: string;
}

/**
 * Response body for `POST /api/rooms/{roomId}/join`. `joinKind` lets the
 * UI distinguish first-time join (welcome banner) from resume (suppress
 * banner, scroll to last unread).
 */
export interface RoomJoinOutput {
  readonly roomId: string;
  readonly nickname: string;
  readonly participants: readonly string[];
  readonly readReceipts: Readonly<Record<string, number>>;
  readonly lastSeq: number;
  readonly joinKind: 'created' | 'resumed' | 'newJoined';
  readonly announcement?: string;
  /**
   * Per-participant "show my read receipts" preference, keyed by nickname.
   * Missing entries default to `true` (rooms created before this field
   * existed continue to display read state). When the *other* participant's
   * value is `false`, the viewer's MessageList hides the read/unread
   * indicator on own messages.
   */
  readonly participantShowReadReceipt?: Readonly<Record<string, boolean>>;
  /** Owner's nickname (rooms created via the registered-user flow). */
  readonly ownerNickname?: string;
  /** Configured guest message limit (`0` = unlimited). */
  readonly guestMessageLimit?: number;
  /** Current value of the consecutive-guest-message counter. */
  readonly consecutiveGuestMessages?: number;
  /** オーナーが登録したテンプレートメッセージ一覧。未登録時は空配列。 */
  readonly templates?: readonly string[];
}

/**
 * Wire representation of a single chat message. Returned by the messages
 * endpoints and republished verbatim as the SignalR `newMessage` payload.
 */
export interface Message {
  readonly id: string;
  readonly roomId: string;
  readonly seq: number;
  readonly senderNickname: string;
  readonly content: string;
  readonly sentAt: string;
}

/**
 * UI-layer view of a message. Extends the wire `Message` with an optional
 * `pending` marker for client-side optimistic messages that have been shown
 * in the transcript but not yet confirmed by the server.
 *
 * `pending` is `undefined` for every server-confirmed message (the common
 * case), so a `DisplayMessage` is structurally a `Message` until proven
 * otherwise. Optimistic entries synthesise the `Message` fields locally
 * (`id` = client temp id, `seq` = a sentinel placed after the newest
 * confirmed message) so `MessageList` can render them with the same code
 * path as real messages.
 */
export interface DisplayMessage extends Message {
  readonly pending?: {
    /** Client-generated id correlating the optimistic entry to its send call. */
    readonly tempId: string;
    /** `sending` while the POST is in flight; `failed` once it errors. */
    readonly status: 'sending' | 'failed';
    /** The send failure, present only when `status === 'failed'`. */
    readonly error?: ChatApiError;
  };
}

/**
 * One page of messages returned by `GET /api/rooms/{roomId}/messages`.
 * `oldestSeqInPage` doubles as the next `beforeSeq` cursor — the design
 * keeps pagination stateless by avoiding opaque continuation tokens.
 */
export interface MessageListPage {
  readonly items: readonly Message[];
  readonly hasMore: boolean;
  readonly oldestSeqInPage: number | null;
}

/** Request body for `POST /api/rooms/{roomId}/messages`. */
export interface SendMessageRequest {
  readonly nickname: string;
  readonly content: string;
}

/** Optional pagination params for `GET /api/rooms/{roomId}/messages`. */
export interface ListMessagesOptions {
  readonly beforeSeq?: number;
  readonly limit?: number;
}

/** Request body for `POST /api/rooms/{roomId}/read`. */
export interface MarkReadRequest {
  readonly nickname: string;
  readonly lastReadSeq: number;
}

/** Response body for `POST /api/rooms/{roomId}/read`. */
export interface ReadReceiptUpdate {
  readonly nickname: string;
  readonly lastReadSeq: number;
  readonly updatedAt: string;
}

/** Request body for `POST /api/negotiate`. */
export interface NegotiateRequest {
  readonly roomId: string;
  readonly nickname: string;
  readonly userId: string;
}

/** Response body for `POST /api/negotiate`. */
export interface SignalRConnectionInfo {
  readonly url: string;
  readonly accessToken: string;
}

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Strongly-typed REST surface. Hooks/components depend on this interface
 * (not the concrete class) so they remain mockable in unit tests.
 */
export interface ChatApi {
  join(roomId: string, request: JoinRoomRequest): Promise<ApiResult<RoomJoinOutput>>;
  sendMessage(roomId: string, request: SendMessageRequest): Promise<ApiResult<Message>>;
  listMessages(
    roomId: string,
    options?: ListMessagesOptions,
  ): Promise<ApiResult<MessageListPage>>;
  markRead(roomId: string, request: MarkReadRequest): Promise<ApiResult<ReadReceiptUpdate>>;
  negotiate(request: NegotiateRequest): Promise<ApiResult<SignalRConnectionInfo>>;
}

/** Factory options. Both fields are optional so production code can call
 * `createChatApi()` with no arguments and get sensible defaults. */
export interface CreateChatApiOptions {
  /** Prefix prepended to every endpoint path. Defaults to `''` (same origin). */
  readonly baseUrl?: string;
  /** Injected fetch implementation. Defaults to `globalThis.fetch`. */
  readonly fetch?: typeof fetch;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

/**
 * Build a `ChatApi` bound to the given baseUrl and fetch implementation.
 *
 * Why a factory instead of class instances?
 *   - The interface is the contract; tests inject a `vi.fn()` to verify
 *     wire shape without standing up MSW.
 *   - No mutable state — every call is a pure function over its args, so
 *     we can return a frozen object literal.
 */
export function createChatApi(options: CreateChatApiOptions = {}): ChatApi {
  const baseUrl = options.baseUrl ?? '';
  // Capture fetch at construction so re-binding `globalThis.fetch` after
  // construction (rare, but possible in tests) doesn't affect the
  // returned instance. The fallback at *call* time is intentional for
  // the no-args factory path.
  const fetchImpl: typeof fetch =
    options.fetch ?? ((...args) => globalThis.fetch(...args));

  /** Common request helper: builds headers, parses body, classifies errors. */
  async function request<T>(
    path: string,
    init: { method: 'GET' | 'POST'; body?: unknown },
  ): Promise<ApiResult<T>> {
    const url = `${baseUrl}${path}`;
    const headers: Record<string, string> = {};
    let body: BodyInit | undefined;

    if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(init.body);
    }

    let response: Response;
    try {
      response = await fetchImpl(url, { method: init.method, headers, body });
    } catch (cause) {
      // Network-level failure (DNS, TLS, abort, offline). The caller can
      // present a generic "couldn't reach the server" message; the raw
      // cause is preserved for logging.
      return { ok: false, error: { kind: 'networkError', cause } };
    }

    if (response.ok) {
      // 200–299: parse body as JSON. We don't have a 204-returning endpoint
      // in this contract, so an empty body would itself be a server bug.
      const value = (await safeReadJson(response)) as T;
      return { ok: true, value };
    }

    // Non-2xx: classify by status and surface the typed error.
    const error = await mapErrorStatus(response);
    return { ok: false, error };
  }

  /** Build a query string from a flat options bag, omitting undefined values. */
  function buildQuery(params: Record<string, string | number | undefined>): string {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) continue;
      usp.set(k, String(v));
    }
    const s = usp.toString();
    return s.length === 0 ? '' : `?${s}`;
  }

  return Object.freeze<ChatApi>({
    join(roomId, body) {
      return request<RoomJoinOutput>(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        body,
      });
    },

    sendMessage(roomId, body) {
      return request<Message>(`/api/rooms/${roomId}/messages`, {
        method: 'POST',
        body,
      });
    },

    listMessages(roomId, options = {}) {
      const query = buildQuery({
        beforeSeq: options.beforeSeq,
        limit: options.limit,
      });
      return request<MessageListPage>(`/api/rooms/${roomId}/messages${query}`, {
        method: 'GET',
      });
    },

    markRead(roomId, body) {
      return request<ReadReceiptUpdate>(`/api/rooms/${roomId}/read`, {
        method: 'POST',
        body,
      });
    },

    negotiate(body) {
      return request<SignalRConnectionInfo>('/api/negotiate', {
        method: 'POST',
        body,
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Error / body helpers (private)
// ---------------------------------------------------------------------------

/**
 * Attempt to parse `response` as JSON. Falls back to returning the raw
 * text (or `null`) so callers can still surface a `serverError.detail`
 * even when an upstream proxy injects an HTML error page.
 */
async function safeReadJson(response: Response): Promise<unknown> {
  // Some non-2xx responses may have empty bodies (e.g. 403 with no detail).
  // `Response.json()` throws on empty bodies, so we read text first.
  let text: string;
  try {
    text = await response.text();
  } catch {
    return null;
  }
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Parse the `Retry-After` header. The HTTP spec allows both an integer
 * (delta seconds) and an HTTP-date; the Secret Chat backend only ever
 * emits the integer form, so date parsing is intentionally skipped —
 * we'd just classify any unparseable value as 0 and let the UI decide.
 */
function parseRetryAfter(response: Response): number {
  const raw = response.headers.get('Retry-After');
  if (raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Translate an HTTP error response into the discriminated union variant
 * documented in `design.md` §Function Handler Layer. Anything outside the
 * known set lands in `serverError` so the caller sees a stable shape.
 */
async function mapErrorStatus(response: Response): Promise<ChatApiError> {
  const status = response.status;
  const body = await safeReadJson(response);

  switch (status) {
    case 400: {
      // Server always wraps the validation failure in
      //   `{ error: { kind: 'invalidInput', detail } }`.
      // Extract `detail` defensively — older or third-party 400s might
      // omit it, in which case we leave it undefined.
      const detail = extractDetail(body);
      return { kind: 'invalidInput', status: 400, detail };
    }
    case 403:
      return { kind: 'notJoined', status: 403 };
    case 404:
      return { kind: 'roomNotFound', status: 404 };
    case 409:
      return { kind: 'roomFull', status: 409 };
    case 422: {
      const word = extractBannedWord(body);
      return { kind: 'bannedWord', status: 422, word };
    }
    case 423: {
      const limit = extractGuestLimit(body);
      return { kind: 'guestLimitReached', status: 423, limit };
    }
    case 429:
      return {
        kind: 'rateLimited',
        status: 429,
        retryAfterSeconds: parseRetryAfter(response),
      };
    case 503:
      return {
        kind: 'concurrencyConflict',
        status: 503,
        retryAfterSeconds: parseRetryAfter(response),
      };
    default:
      return { kind: 'serverError', status, detail: body };
  }
}

/**
 * Pull `error.detail` out of the standard error envelope without throwing
 * when the body has an unexpected shape. Returns `undefined` so the caller
 * can decide whether to render a fallback message.
 */
function extractDetail(body: unknown): unknown {
  if (typeof body !== 'object' || body === null) return undefined;
  const errorField = (body as { error?: unknown }).error;
  if (typeof errorField !== 'object' || errorField === null) return undefined;
  return (errorField as { detail?: unknown }).detail;
}

/**
 * Pull the configured guest message limit out of a 423 response envelope.
 * The sendMessage handler emits `{ error: { kind, limit } }`; if `limit` is
 * missing for any reason, fall back to 0 (the UI treats this as "limit
 * reached but exact cap unknown").
 */
function extractGuestLimit(body: unknown): number {
  if (typeof body !== 'object' || body === null) return 0;
  const errorField = (body as { error?: unknown }).error;
  if (typeof errorField !== 'object' || errorField === null) return 0;
  const limit = (errorField as { limit?: unknown }).limit;
  return typeof limit === 'number' && Number.isFinite(limit) ? limit : 0;
}

/**
 * Pull the matched banned word out of a 422 response envelope. Falls back
 * to an empty string when missing so the UI can still render a generic
 * "NGワードを含む" message rather than crashing.
 */
function extractBannedWord(body: unknown): string {
  if (typeof body !== 'object' || body === null) return '';
  const errorField = (body as { error?: unknown }).error;
  if (typeof errorField !== 'object' || errorField === null) return '';
  const word = (errorField as { word?: unknown }).word;
  return typeof word === 'string' ? word : '';
}
